import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout';
import ThemeContext from '../../context/ThemeContext';

const PrivacyPolicyPage = () => {
  const { theme } = useContext(ThemeContext) || {};
  const isDark = theme === 'dark';

  const Section = ({ title, children }) => (
    <div className="mb-10 last:mb-0">
      <h2 className={`text-xl font-bold mb-4 flex items-center gap-3 ${
        isDark ? 'text-slate-100' : 'text-slate-900'
      }`}>
        {title}
      </h2>
      <div className={`prose max-w-none ${
        isDark ? 'prose-invert text-slate-300' : 'text-slate-600'
      }`}>
        {children}
      </div>
    </div>
  );

  return (
    <LegalLayout 
      title="Politique de Confidentialité" 
      subtitle="Dernière mise à jour : 9 Décembre 2025"
    >
      <div className="space-y-8">
        
        <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20 mb-8">
          <p className={`text-sm ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
            La présente Politique de Confidentialité explique comment Muzo / Muzo Studio (ci-après « le Service ») collecte, utilise, traite et protège les données personnelles de ses utilisateurs conformément au Règlement Général sur la Protection des Données (RGPD) et à la législation française applicable.
            En utilisant le Service, l’utilisateur accepte les pratiques décrites ci-dessous.
          </p>
        </div>

        <Section title="1. Responsable du traitement (RGPD)">
          <p>
            Le responsable du traitement est l'Éditeur du Service, dont les coordonnées complètes sont disponibles dans les <Link to="/legal/mentions-legales" className="text-indigo-500 hover:underline">Mentions Légales</Link>.
          </p>
          <p className="mt-2">
            Email de contact : <a href="mailto:contact@muzo-studio.app" className="text-indigo-500 hover:underline">contact@muzo-studio.app</a>
          </p>
          <p className="mt-2 text-sm italic">(ci-après « l’Éditeur » ou « nous »)</p>
        </Section>

        <Section title="2. Données collectées">
          <p>Nous collectons uniquement les données nécessaires au fonctionnement technique du Service, à la facturation, et à la sécurité.</p>
          
          <h3 className="font-bold text-lg mb-2 mt-4">2.1 Données fournies lors de l’inscription</h3>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Adresse email</li>
            <li>Mot de passe (hashé et jamais stocké en clair)</li>
            <li>Identifiant unique d’utilisateur</li>
            <li>Informations de profil (éventuellement avatar, nom d’utilisateur)</li>
          </ul>

          <h3 className="font-bold text-lg mb-2 mt-4">2.2 Données nécessaires au fonctionnement du Service</h3>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>URLs ou fichiers vidéo fournis par l’utilisateur</li>
            <li>Vidéos générées ou traitées (stockage temporaire)</li>
            <li>Historique des traitements</li>
            <li>Paramètres choisis (résolution, upscale, etc.)</li>
          </ul>

          <h3 className="font-bold text-lg mb-2 mt-4">2.3 Données techniques</h3>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Adresse IP</li>
            <li>Logs serveurs (erreurs, accès, diagnostics)</li>
            <li>Informations liées au device ou au navigateur</li>
            <li>Horodatages (création de compte, connexions, actions)</li>
          </ul>

          <h3 className="font-bold text-lg mb-2 mt-4">2.4 Données de paiement</h3>
          <p>Traitement exclusivement par Stripe :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Informations de facturation</li>
            <li>Montant et date des paiements</li>
            <li>Identifiant Stripe</li>
            <li>Historique des transactions</li>
          </ul>
          <p className="mt-2 font-bold text-amber-500">⚠️ Nous ne stockons jamais les numéros de carte bancaire.</p>

          <h3 className="font-bold text-lg mb-2 mt-4">2.5 Données issues de services tiers</h3>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Firebase (authentification, base de données en temps réel)</li>
            <li>RunPod (exécution GPU, identifiant des jobs, logs techniques)</li>
            <li>CDN tiers (téléchargement de vidéos Sora ou autres IA)</li>
          </ul>
        </Section>

        <Section title="3. Finalités du traitement">
          <p>Nous utilisons les données pour :</p>
          
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <h4 className="font-bold mb-2">✔ Fournir le fonctionnement du Service</h4>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li>traitement vidéo, upscale, conversion</li>
                <li>gestion des comptes et authentification</li>
                <li>système de crédits, abonnements, facturation</li>
                <li>envoi des résultats et historique d’utilisation</li>
              </ul>
            </div>

            <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <h4 className="font-bold mb-2">✔ Sécuriser la plateforme</h4>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li>détection d’abus ou contenus interdits</li>
                <li>prévention des attaques ou fraudes</li>
                <li>limitation d’usage non autorisé</li>
              </ul>
            </div>

            <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <h4 className="font-bold mb-2">✔ Améliorer l’expérience utilisateur</h4>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li>optimisation des performances</li>
                <li>amélioration des modèles d’IA</li>
                <li>remontée d’erreurs et débogage</li>
              </ul>
            </div>

            <div className={`p-4 rounded-lg border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <h4 className="font-bold mb-2">✔ Respecter nos obligations légales</h4>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li>comptabilité</li>
                <li>gestion des paiements</li>
                <li>obligations fiscales ou de facturation</li>
              </ul>
            </div>
          </div>
        </Section>

        <Section title="4. Base légale du traitement (article 6 RGPD)">
          <p>Nous traitons les données sur les bases légales suivantes :</p>
          <ul className="list-none mt-4 space-y-3">
            <li>
              <strong>✔ Exécution du contrat (article 6.1.b RGPD)</strong> : création de compte, fourniture du service vidéo, gestion des crédits, exécution d’un abonnement.
            </li>
            <li>
              <strong>✔ Intérêt légitime (article 6.1.f RGPD)</strong> : sécurité du service, prévention des abus, amélioration continue.
            </li>
            <li>
              <strong>✔ Obligation légale (article 6.1.c RGPD)</strong> : obligations fiscales et comptables.
            </li>
            <li>
              <strong>✔ Consentement (article 6.1.a RGPD)</strong> : utilisation de cookies non essentiels, communications marketing (si activées ultérieurement).
            </li>
          </ul>
        </Section>

        <Section title="5. Destinataires des données">
          <p>Les données sont transmises uniquement à des prestataires strictement nécessaires au fonctionnement du Service :</p>
          <div className="mt-4 space-y-4">
            <div>
              <span className="font-bold text-indigo-500">🔹 Stripe</span>
              <p className="text-sm ml-6">Paiements, facturation, gestion des abonnements.</p>
            </div>
            <div>
              <span className="font-bold text-indigo-500">🔹 Firebase (Google Cloud)</span>
              <p className="text-sm ml-6">Authentification, stockage, synchronisation en temps réel.</p>
            </div>
            <div>
              <span className="font-bold text-indigo-500">🔹 RunPod</span>
              <p className="text-sm ml-6">Exécution GPU des traitements vidéo.</p>
            </div>
            <div>
              <span className="font-bold text-indigo-500">🔹 Hébergeurs & CDN</span>
              <p className="text-sm ml-6">(Vercel, OVH, Scaleway, Cloudflare, ou autres selon configuration du site)</p>
            </div>
            <div>
              <span className="font-bold text-indigo-500">🔹 Autorités légales</span>
              <p className="text-sm ml-6">Uniquement en cas d’obligation légale (abus graves, contenus illégaux, enquête judiciaire).</p>
            </div>
          </div>
          <p className="mt-4 font-semibold">Nous ne vendons jamais de données à des tiers.</p>
        </Section>

        <Section title="6. Durée de conservation">
          <p>Chaque catégorie de données est conservée pour une durée proportionnée :</p>
          <ul className="list-none mt-4 space-y-3">
            <li>
              <strong>✔ Compte utilisateur</strong> : Jusqu’à suppression volontaire du compte.
            </li>
            <li>
              <strong>✔ Vidéos / fichiers / outputs</strong> : Conservation temporaire uniquement. Durée : le temps du traitement + mise à disposition pour téléchargement. Les vidéos peuvent être supprimées à tout moment après finalisation du traitement.
            </li>
            <li>
              <strong>✔ Logs techniques</strong> : 30 à 90 jours selon les besoins (anti-abus, sécurité).
            </li>
            <li>
              <strong>✔ Historique de paiements (Stripe)</strong> : 10 ans (obligation comptable).
            </li>
            <li>
              <strong>✔ Emails / support</strong> : 12 à 24 mois selon le contexte.
            </li>
          </ul>
        </Section>

        <Section title="7. Stockage et sécurité">
          <p>Nous mettons en œuvre des mesures de sécurité adaptées :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>stockage chiffré</li>
            <li>accès restreint aux systèmes</li>
            <li>authentification sécurisée</li>
            <li>suppression automatique des vidéos traitées</li>
            <li>isolation des traitements GPU</li>
            <li>environnement Docker non-root</li>
            <li>mise à jour régulière des dépendances</li>
          </ul>
          <p className="mt-4">
            Aucun système n’étant infaillible, l'utilisateur est invité à utiliser un mot de passe complexe, ne pas partager ses identifiants et télécharger ses vidéos dès qu'elles sont prêtes.
          </p>
        </Section>

        <Section title="8. Transfert des données hors UE">
          <p>
            Certains prestataires (ex : Stripe, RunPod, Firebase) sont situés hors de l’Union européenne.
            Les transferts sont réalisés via des clauses contractuelles types (SCC) et conformément au RGPD.
          </p>
        </Section>

        <Section title="9. Droits de l’utilisateur (RGPD)">
          <p>L’utilisateur dispose des droits suivants :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Droit d’accès</li>
            <li>Droit de rectification</li>
            <li>Droit de suppression (droit à l’oubli)</li>
            <li>Droit à la portabilité</li>
            <li>Droit de limiter le traitement</li>
            <li>Droit d’opposition (selon base légale)</li>
            <li>Droit de retirer son consentement</li>
          </ul>
          <p className="mt-4">
            Pour exercer ses droits : <a href="mailto:contact@muzo-studio.app" className="text-indigo-500 hover:underline">contact@muzo-studio.app</a>
          </p>
          <p className="mt-2 text-sm">Une réponse sera apportée sous 30 jours maximum.</p>
        </Section>

        <Section title="10. Suppression du compte">
          <p>L’utilisateur peut demander la suppression de son compte à tout moment.</p>
          <p className="mt-2 font-semibold">Effets :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>suppression des données de profil</li>
            <li>suppression de l’historique (hors contraintes légales Stripe)</li>
            <li>suppression des vidéos stockées (si encore disponibles)</li>
            <li>annulation du solde de crédits expirés ou non utilisés (non remboursables)</li>
          </ul>
        </Section>

        <Section title="11. Cookies">
          <p>Le Service utilise :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Cookies techniques</strong> : Indispensables au fonctionnement (auth Firebase, session utilisateur).</li>
            <li><strong>Cookies Stripe</strong> : Nécessaires au paiement sécurisé.</li>
            <li><strong>Aucun cookie marketing par défaut</strong></li>
          </ul>
          <p className="mt-2">
            Une page dédiée explique ce fonctionnement : <Link to="/legal/cookies" className="text-indigo-500 hover:underline">Politique Cookies</Link>
          </p>
        </Section>

        <Section title="12. Modifications de la Politique">
          <p>
            Nous pouvons modifier la présente politique à tout moment.
            Une notification pourra être affichée lors des mises à jour importantes.
          </p>
        </Section>

        <Section title="13. Contact – Délégué à la protection des données">
          <p>
            Pour toute question concernant la confidentialité : <a href="mailto:contact@muzo-studio.app" className="text-indigo-500 hover:underline">contact@muzo-studio.app</a>
          </p>
        </Section>

      </div>
    </LegalLayout>
  );
};

export default PrivacyPolicyPage;
